import { useEffect, useState } from 'react'
import { LogIn, ShieldAlert } from 'lucide-react'
import { ApiError, fetchTestUsers, signIn, translateError, type AppLocale, type TestUser } from '@hk/core'

/* ============================================================================
   W-01 · SIGN IN — the test build.

   There is no password field, and there is not going to be one. The client's PMS
   authenticates people and redirects them to us (baseline, corrected 21 August); we
   never see a credential. Until TQ-01 says HOW we verify what the PMS sends, this
   screen stands in for that redirect by letting a tester choose who to be.

   THE ROLE IS SHOWN AGAINST EVERY NAME on purpose. The whole value of testing with
   real staff is finding out what a supervisor can do that an attendant cannot, and a
   picker that lists eight names without saying which is which turns that into
   guesswork.

   When TQ-01 lands this becomes a redirect and a callback. The screen after it does
   not change, because the session it produces is already the real one.
   ========================================================================= */

const TEXT = {
  en: {
    title: 'Sign in',
    subtitle: 'Choose who you are testing as.',
    warning: 'This is a preview server. There is no password and nothing is verified — '
      + 'anyone who can reach this address can act as any member of staff.',
    manual: 'Or type an employee number',
    go: 'Sign in',
    loading: 'Loading people…',
    noUsers: 'Type the employee number you were given.',
    keyLabel: 'Staging access key',
    keyHelp: 'The one your administrator gave the team. Not your password — everybody testing '
      + 'uses the same one, and it is not stored anywhere on this device.',
  },
  ar: {
    title: 'تسجيل الدخول',
    subtitle: 'اختر الشخص الذي تختبر باسمه.',
    warning: 'هذا خادم معاينة. لا توجد كلمة مرور ولا يتم التحقق من شيء — يمكن لأي شخص '
      + 'يصل إلى هذا العنوان أن يتصرف باسم أي موظف.',
    manual: 'أو اكتب رقم الموظف',
    go: 'دخول',
    loading: 'جارٍ تحميل الأشخاص…',
    noUsers: 'اكتب رقم الموظف الذي أُعطي لك.',
    keyLabel: 'مفتاح الدخول إلى خادم الاختبار',
    keyHelp: 'المفتاح الذي أعطاه مدير النظام للفريق. ليس كلمة مرورك — جميع المختبرين '
      + 'يستخدمون المفتاح نفسه، ولا يُحفظ على هذا الجهاز.',
  },
} as const

const ROLE_LABEL: Record<string, { en: string; ar: string }> = {
  ROOM_ATTENDANT: { en: 'Room attendant', ar: 'عامل غرف' },
  FLOOR_SUPERVISOR: { en: 'Floor supervisor', ar: 'مشرف طابق' },
  HOUSEKEEPING_MANAGER: { en: 'Housekeeping manager', ar: 'مدير التدبير الفندقي' },
  STORE_KEEPER: { en: 'Store keeper', ar: 'أمين المخزن' },
  FRONT_DESK: { en: 'Front desk', ar: 'الاستقبال' },
  ADMINISTRATOR: { en: 'Administrator', ar: 'مدير النظام' },
}

export function SignIn(
  { locale, onSignedIn }: { locale: AppLocale; onSignedIn: () => void },
) {
  const t = TEXT[locale]
  const [users, setUsers] = useState<TestUser[] | null>(null)
  const [employeeId, setEmployeeId] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  /* THE FIELD APPEARS WHEN THE SERVER ASKS FOR IT, and not before.

     A deployment either has a site key or it does not, and the client cannot know which
     until it tries — so it tries, and reveals the field on the one refusal that means
     "you need a key". Showing it unconditionally would put a box on the development and
     production sign-in screens that nothing on earth accepts a value into. */
  const [needsKey, setNeedsKey] = useState(false)
  const [stagingKey, setStagingKey] = useState('')

  useEffect(() => { void fetchTestUsers().then(setUsers) }, [])

  const attempt = async (assertion: string) => {
    setBusy(assertion)
    setError(null)
    try {
      await signIn(assertion, stagingKey.trim() || undefined)
      onSignedIn()
    } catch (failure) {
      // Translated from the code, like every other error in the product. The server
      // never sends a sentence — `auth.noAccount` is a different problem from
      // `auth.assertionRejected` and the person to call is different too.
      if (failure instanceof ApiError && failure.code === 'auth.stagingKeyRequired')
        setNeedsKey(true)
      setError(translateError(failure, locale).message)
      setBusy(null)
    }
  }

  return (
    <div className="min-h-dvh bg-muted/40 flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-hk border border-border bg-card shadow-sm">

        <div className="flex items-start gap-2.5 rounded-t-hk border-b-2 border-[#b91c1c]
                        bg-[#fef2f2] px-4 py-2.5 text-[12px] text-[#7f1d1d]
                        dark:bg-[#2b0f0f] dark:text-[#fecaca]">
          <ShieldAlert className="mt-px size-4 shrink-0" />
          <p className="leading-snug">{t.warning}</p>
        </div>

        <div className="p-5">
          <h1 className="text-lg font-semibold text-foreground">{t.title}</h1>
          <p className="mt-0.5 text-[13px] text-muted-foreground">{t.subtitle}</p>

          {error && (
            <p role="alert" className="mt-3 rounded-hk bg-[#fef2f2] px-3 py-2 text-[12.5px] text-[#b91c1c]
                                       dark:bg-[#2b0f0f] dark:text-[#fca5a5]">
              {error}
            </p>
          )}

          {needsKey && (
            <div className="mt-4 flex flex-col gap-1.5">
              <label htmlFor="staging-key" className="text-[12px] font-semibold text-foreground">
                {t.keyLabel}
              </label>
              <input
                id="staging-key"
                /* `password` so it is not shoulder-read and not offered to autofill as a
                   username. It is still typed by a person and still shared by a team —
                   the input type is a courtesy, not a security property. */
                type="password"
                autoComplete="off"
                value={stagingKey}
                onChange={(e) => setStagingKey(e.target.value)}
                className="h-9 rounded-hk border border-input bg-background px-3 text-[13px]"
              />
              <p className="text-[11.5px] text-muted-foreground">{t.keyHelp}</p>
            </div>
          )}

          <div className="mt-4 flex flex-col gap-1.5">
            {users === null && (
              <p className="text-[13px] text-muted-foreground">{t.loading}</p>
            )}

            {users?.map((user) => {
              const role = ROLE_LABEL[user.roleCode]
              return (
                <button
                  key={user.userId}
                  type="button"
                  disabled={busy !== null}
                  onClick={() => void attempt(user.employeeId)}
                  className="flex items-center gap-3 rounded-hk border border-border px-3 py-2.5
                             text-start hover:bg-muted disabled:opacity-50 cursor-pointer"
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full
                                   bg-primary/10 text-[11px] font-semibold text-primary">
                    {user.employeeId.replace('T-', '')}
                  </span>
                  <span className="min-w-0">
                    {/* `bdi` so a Latin name inside an Arabic page does not render with
                        its full stop on the wrong end — the defect found in August. */}
                    <bdi className="block truncate text-[13.5px] font-medium text-foreground">
                      {locale === 'ar' ? user.displayNameAr ?? user.displayName : user.displayName}
                    </bdi>
                    <span className="block text-[11.5px] text-muted-foreground">
                      {role ? role[locale] : user.roleName}
                    </span>
                  </span>
                </button>
              )
            })}

            {users?.length === 0 && (
              <p className="text-[13px] text-muted-foreground">{t.noUsers}</p>
            )}
          </div>

          <form
            className="mt-4 border-t border-border pt-4"
            onSubmit={(event) => { event.preventDefault(); void attempt(employeeId.trim()) }}
          >
            <label className="block text-[12px] font-medium text-muted-foreground" htmlFor="employeeId">
              {t.manual}
            </label>
            <div className="mt-1.5 flex gap-2">
              <input
                id="employeeId"
                value={employeeId}
                onChange={(event) => setEmployeeId(event.target.value)}
                // Human-entered text always gets dir="auto" — the rule from the August
                // defect where "Zahra A." rendered as ".Zahra A" in an Arabic form.
                dir="auto"
                className="min-w-0 flex-1 rounded-hk border border-border bg-background px-2.5 py-1.5 text-[13px]"
              />
              <button
                type="submit"
                disabled={busy !== null || employeeId.trim() === ''}
                className="inline-flex items-center gap-1.5 rounded-hk bg-primary px-3 py-1.5
                           text-[13px] font-medium text-primary-foreground
                           disabled:opacity-50 cursor-pointer"
              >
                <LogIn className="size-3.5" />
                {t.go}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
